import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { renderAsync } from 'docx-preview';
import TvecLayout from '../../components/TvecLayout';
import TvecDownloadAll from '../../components/TvecDownloadAll';
import { SheetTable, parseSheet } from '../../components/TvecSheetTable';
import { ALL_COURSES_ID, COURSE_PROGRAMMES, useTvecCourse } from '../../utils/tvecCourse';
import {
  KIND_LABEL,
  PROCESSES,
  PROGRAMMES,
  TEMPLATE_ITEMS,
  formatSize,
  templateUrl,
} from '../../utils/tvecTemplates';

// Every preview is read from the very file that is downloaded; nothing is converted or
// re-saved. Excel previews show content and layout (cells, merges, column widths); fonts,
// colours and borders live only in the file itself.
// Shows every sheet of the workbook; a tab bar appears when there is more than one.
function SheetPreview({ item }) {
  const [sheets, setSheets] = useState(null);
  const [active, setActive] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSheets(null);
    setError('');
    fetch(templateUrl(item))
      .then((res) => {
        if (!res.ok) throw new Error('missing');
        return res.arrayBuffer();
      })
      .then((buf) => {
        if (cancelled) return;
        const wb = XLSX.read(buf, { type: 'array' });
        setSheets(wb.SheetNames.map((name) => parseSheet(name, wb.Sheets[name])));
        setActive(0);
      })
      .catch(() => { if (!cancelled) setError('Could not load the template preview.'); });
    return () => { cancelled = true; };
  }, [item]);

  if (error) return <p className="py-10 text-center text-sm text-rose-600">{error}</p>;
  if (!sheets) return <p className="py-10 text-center text-sm text-slate-500">Loading preview…</p>;

  const current = sheets[active] || sheets[0];
  return (
    <div>
      {sheets.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
          {sheets.map((sheet, i) => (
            <button
              key={sheet.name}
              type="button"
              onClick={() => setActive(i)}
              className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition ${i === active ? 'bg-sky-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      ) : null}
      <SheetTable sheet={current} headerRows={current === sheets[0] ? item.headerRows || 1 : 1} legacy={Boolean(item.legacy)} />
    </div>
  );
}

// Renders the Word file itself (pages, tables, fonts) in the browser.
function DocxPreview({ item }) {
  const hostRef = useRef(null);
  const [state, setState] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    fetch(templateUrl(item))
      .then((res) => {
        if (!res.ok) throw new Error('missing');
        return res.arrayBuffer();
      })
      .then((buf) => {
        if (cancelled || !hostRef.current) return null;
        hostRef.current.innerHTML = '';
        return renderAsync(buf, hostRef.current, undefined, {
          className: 'docx',
          inWrapper: true,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
        });
      })
      .then(() => { if (!cancelled) setState('ready'); })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [item]);

  return (
    <div>
      {state === 'loading' ? <p className="py-10 text-center text-sm text-slate-500">Loading preview…</p> : null}
      {state === 'error' ? (
        <p className="py-10 text-center text-sm text-rose-600">Could not preview this document. Use Download to open it in Word.</p>
      ) : null}
      <div ref={hostRef} className="overflow-x-auto" style={{ display: state === 'error' ? 'none' : 'block' }} />
    </div>
  );
}

// PDFs open in the browser's own viewer.
function PdfPreview({ item }) {
  return (
    <iframe
      title={item.name}
      src={templateUrl(item)}
      className="h-[72vh] w-full rounded-lg border border-slate-200 bg-white"
    />
  );
}

function DownloadLink({ item, label }) {
  return (
    <a
      href={templateUrl(item)}
      download={item.file}
      className="rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white no-underline hover:bg-sky-700"
    >
      {label}
    </a>
  );
}

function PreviewModal({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const note = item.legacy
    ? null
    : item.kind === 'excel'
      ? 'Preview shows the content and layout. Download the file for the exact original formatting.'
      : 'Preview of the original document. Download the file to open it in its own application.';

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 px-3 py-6" onClick={onClose}>
      <div
        className="flex max-h-full w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-slate-900">{item.name}</h3>
            <p className="truncate text-xs text-slate-500">{item.file}</p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <DownloadLink item={item} label={`Download ${KIND_LABEL[item.kind]}`} />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              Close
            </button>
          </div>
        </div>
        {note ? <p className="border-b border-slate-100 bg-slate-50 px-5 py-2 text-xs text-slate-500">{note}</p> : null}
        <div className="overflow-auto bg-slate-50 p-5">
          {item.kind === 'word' ? <DocxPreview item={item} /> : item.kind === 'pdf' ? <PdfPreview item={item} /> : <SheetPreview item={item} />}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const KIND_STYLE = {
  excel: { badge: 'bg-emerald-50 text-emerald-700', icon: 'bg-emerald-50 text-emerald-600' },
  word: { badge: 'bg-sky-50 text-sky-700', icon: 'bg-sky-50 text-sky-600' },
  pdf: { badge: 'bg-rose-50 text-rose-700', icon: 'bg-rose-50 text-rose-600' },
};

function KindIcon({ kind }) {
  return (
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${KIND_STYLE[kind].icon}`}>
      <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
        {kind === 'excel'
          ? <path d="M9 17v-6m4 6v-3m4 3V8M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
          : <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />}
      </svg>
    </span>
  );
}

function TemplateRow({ item, onPreview }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 transition hover:bg-sky-50/40">
      <KindIcon kind={item.kind} />
      <div className="min-w-0 flex-1 basis-56">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-words text-sm font-semibold text-slate-900">{item.name}</span>
          {item.code ? (
            <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">{item.code}</span>
          ) : null}
        </p>
        <p className="break-all text-xs text-slate-500">
          {item.file}
          <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold ${KIND_STYLE[item.kind].badge}`}>{KIND_LABEL[item.kind]}</span>
          {item.size ? <span className="ml-2 text-slate-400">{formatSize(item.size)}</span> : null}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={() => onPreview(item)}
          className="rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
        >
          Preview
        </button>
        <DownloadLink item={item} label="Download" />
      </div>
    </li>
  );
}

// One process stage (e.g. "Course plan"): a collapsible block of rows, with sub-headings
// such as "Sem 1" or "Module 1 / Tutorials" when the original folder had them.
function ProcessSection({ title, hint, items, open, onToggle, onPreview }) {
  const subs = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = it.sub || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return Array.from(map.entries());
  }, [items]);

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-slate-900">{title}</span>
          {hint ? <span className="block truncate text-xs text-slate-500">{hint}</span> : null}
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700">{items.length}</span>
          <svg className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="border-t border-slate-100">
          {subs.map(([sub, rows]) => (
            <div key={sub || 'all'}>
              {sub ? <p className="bg-slate-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{sub}</p> : null}
              <ul className="divide-y divide-slate-100">
                {rows.map((it) => (
                  <TemplateRow key={it.id} item={it} onPreview={onPreview} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

// Sections below the selected course's own documents.
const EXTRA_GROUPS = [
  { id: 'common', title: 'Common material', hint: 'Shared by every course' },
  { id: 'typed', title: 'Other formats (typed from pasted text)', hint: 'No original file was supplied for these. Send the original and it will replace the version here.' },
];

export default function TvecTemplate() {
  const { course } = useTvecCourse(); // chosen in the top bar
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState('');
  const [toggled, setToggled] = useState({}); // section key -> boolean (the user's own choice)
  const [allOpen, setAllOpen] = useState(null); // null = default, true / false = expand / collapse all

  const programmeOf = useMemo(() => Object.fromEntries(PROGRAMMES.map((p) => [p.id, p])), []);
  const searching = search.trim().length > 0;

  const isAll = course.id === ALL_COURSES_ID;

  // One course, or every course when "All courses" is selected.
  const courseItems = useMemo(
    () => TEMPLATE_ITEMS.filter((it) => (isAll ? COURSE_PROGRAMMES.some((c) => c.id === it.programme) : it.programme === course.id)),
    [course.id, isAll],
  );

  // Per course: its process stages, in the order the documents are used.
  const courseGroups = useMemo(
    () => (isAll ? COURSE_PROGRAMMES : [course])
      .map((c) => ({
        course: c,
        count: courseItems.filter((it) => it.programme === c.id).length,
        sections: PROCESSES
          .map((proc) => ({
            key: `${c.id}:${proc.id}`,
            title: proc.label,
            hint: proc.hint,
            items: courseItems.filter((it) => it.programme === c.id && it.process === proc.id),
          }))
          .filter((sec) => sec.items.length > 0),
      }))
      .filter((g) => g.sections.length > 0),
    [course, isAll, courseItems],
  );
  const courseSectionCount = courseGroups.reduce((n, g) => n + g.sections.length, 0);

  const extraSections = useMemo(
    () => EXTRA_GROUPS
      .map((g) => ({ key: `extra:${g.id}`, title: g.title, hint: g.hint, items: TEMPLATE_ITEMS.filter((it) => it.programme === g.id) }))
      .filter((s) => s.items.length > 0),
    [],
  );

  // Searching looks across every course (grouped by course), not just the selected one.
  const searchSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const hits = TEMPLATE_ITEMS.filter((it) =>
      [it.name, it.code, it.file, it.sub, programmeOf[it.programme]?.label, PROCESSES.find((p) => p.id === it.process)?.label]
        .some((v) => String(v || '').toLowerCase().includes(q)));
    const out = [];
    PROGRAMMES.forEach((prog) => {
      PROCESSES.forEach((proc) => {
        const items = hits.filter((it) => it.programme === prog.id && it.process === proc.id);
        if (items.length) out.push({ key: `search:${prog.id}:${proc.id}`, title: proc.label, hint: '', items, programme: prog.label });
      });
    });
    return out;
  }, [search, programmeOf]);

  const isOpen = (s, defaultOpen) => {
    if (searching) return true;
    if (allOpen !== null) return allOpen;
    if (s.key in toggled) return toggled[s.key];
    return defaultOpen;
  };

  const renderSection = (s, defaultOpen) => (
    <ProcessSection
      key={s.key}
      title={s.title}
      hint={s.hint}
      items={s.items}
      open={isOpen(s, defaultOpen)}
      onToggle={() => {
        setAllOpen(null);
        setToggled((prev) => ({ ...prev, [s.key]: !isOpen(s, defaultOpen) }));
      }}
      onPreview={setPreview}
    />
  );

  const hasSections = courseSectionCount + extraSections.length > 1;

  return (
    <TvecLayout title="Template">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">{isAll ? 'Showing' : 'Selected course'}</p>
            <h2 className="text-lg font-semibold text-slate-900">
              {course.label} <span className="font-normal text-slate-400">({courseItems.length} documents{isAll ? ` · ${courseGroups.length} courses` : ''})</span>
            </h2>
            <p className="text-sm text-slate-500">Change the course from the top bar. Preview any template or download it exactly as the original.</p>
          </div>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search all templates…"
            aria-label="Search templates"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 sm:w-80"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600">
            {searching
              ? <>Showing <span className="font-semibold">{searchSections.reduce((n, s) => n + s.items.length, 0)}</span> result(s) across all courses</>
              : isAll
                ? <>Grouped by course, then process stage</>
                : <>Process stages for <span className="font-semibold text-slate-800">{course.short}</span></>}
          </p>
          {!searching ? (
            <div className="flex flex-wrap items-start justify-end gap-2 text-xs font-semibold">
              {hasSections ? (
                <>
                  <button type="button" onClick={() => setAllOpen(true)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-600 hover:bg-slate-100">Expand all</button>
                  <button type="button" onClick={() => setAllOpen(false)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-600 hover:bg-slate-100">Collapse all</button>
                </>
              ) : null}
              <TvecDownloadAll course={course} />
            </div>
          ) : null}
        </div>

        {searching ? (
          searchSections.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-sm text-slate-500">
              No templates match “{search.trim()}”.
            </p>
          ) : (
            <div className="space-y-3">
              {searchSections.map((s, i) => (
                <React.Fragment key={s.key}>
                  {i === 0 || searchSections[i - 1].programme !== s.programme ? (
                    <h3 className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{s.programme}</h3>
                  ) : null}
                  {renderSection(s, true)}
                </React.Fragment>
              ))}
            </div>
          )
        ) : (
          <>
            {courseGroups.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-sm text-slate-500">
                No templates have been added for {course.label} yet.
              </p>
            ) : (
              <div className="space-y-5">
                {courseGroups.map((g) => (
                  <div key={g.course.id} className="space-y-3">
                    {isAll ? (
                      <h3 className="flex items-center gap-2 border-b border-slate-200 pb-2 text-sm font-semibold text-slate-800">
                        {g.course.label}
                        <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700">{g.count}</span>
                      </h3>
                    ) : null}
                    {/* One course: short lists open, long ones (e.g. 15 session plans) collapsed.
                        All courses: everything starts collapsed so the page stays short. */}
                    {g.sections.map((sec) => renderSection(sec, !isAll && sec.items.length <= 8))}
                  </div>
                ))}
              </div>
            )}

            {extraSections.map((s) => (
              <div key={s.key} className="space-y-2 pt-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {s.key === 'extra:common' ? 'For all courses' : 'Other'}
                </h3>
                {renderSection(s, s.key === 'extra:common')}
              </div>
            ))}
          </>
        )}
      </div>
      {preview ? <PreviewModal item={preview} onClose={() => setPreview(null)} /> : null}
    </TvecLayout>
  );
}
